import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { PreloaderIcon } from '../../../../../common/ui/icons';
import { showToast } from '../../../../../common/domain/toast-provider';
import { useInteriorOrganizerStore } from '../../../data/interior-organizer-store';
import { insertStoragePlace, updateStoragePlace } from '../../../data/interior-organizer-data-service';
import { IStoragePlace } from '../../../interior-organizer-interfaces';
import { PlacesTab } from '../tabs-navigation';
import { getCompactMarkdownEditorPlugins } from '../../../../../common/markdown/markdown-editor-provider';
import { MDXEditor, MDXEditorMethods } from '@mdxeditor/editor';

interface IUpsertStoragePlace {
    storagePlace?: IStoragePlace;
    close?: () => void;
}

const UpsertStoragePlace = ({ storagePlace, close }: IUpsertStoragePlace) => {

    const nameRef = useRef<HTMLInputElement>(null);
    const editorRef = useRef<MDXEditorMethods>(null);

    const setStoragePlacesData = useInteriorOrganizerStore(store => store.setStoragePlacesData);
    const setPlacesTab = useInteriorOrganizerStore(store => store.setPlacesTab);

    const [name, setName] = useState('');
    const [nameTouched, setNameTouched] = useState(false);

    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const [depth, setDepth] = useState(0);
    const [isFull, setIsFull] = useState(false);
    const [isLoading, setLoading] = useState(false);

    const isNameValid = !nameTouched || name.trim().length > 0;

    useEffect(() => {
        nameRef?.current?.focus();
    }, []);

    useEffect(() => {
        if(!storagePlace) return;

        editorRef.current?.setMarkdown(storagePlace.description ?? '');

        setName(storagePlace.name);
        setWidth(storagePlace.width || 0);
        setHeight(storagePlace.height || 0);
        setDepth(storagePlace.depth || 0);
        setIsFull(storagePlace.is_full);
    }, [storagePlace]);

    const save = useCallback(async () => {

        if(name.trim().length <= 0) {
            setNameTouched(true);
            return;
        }

        setLoading(true);

        const description = editorRef.current?.getMarkdown() || '';

        const response = storagePlace ?
            await updateStoragePlace(
                storagePlace.id,
                name,
                description,
                width,
                height,
                depth,
                isFull,
            )
            :
            await insertStoragePlace(
                name,
                description,
                width,
                height,
                depth,
                isFull,
            );

        setLoading(false);

        if(!response) {
            showToast('Upsert storage place error.');
            return;
        }

        setStoragePlacesData(response);

        if(!storagePlace) {
            setPlacesTab(PlacesTab.Default);
        }

        if(close) {
            close();
        }
    }, [
        close,
        depth,
        height,
        isFull,
        name,
        setPlacesTab,
        setStoragePlacesData,
        storagePlace,
        width,
    ]);

    const onNameChange = (evt: ChangeEvent<HTMLInputElement>) => {
        setName((evt.target as HTMLInputElement).value);
        setNameTouched(true);
    };

    const onWidthChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const value = Number((evt.target as HTMLInputElement).value) || 0;
        setWidth(value);
    };

    const onHeightChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const value = Number((evt.target as HTMLInputElement).value) || 0;
        setHeight(value);
    };

    const onDepthChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const value = Number((evt.target as HTMLInputElement).value) || 0;
        setDepth(value);
    };

    const onIsFullChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const checked = (evt.target as HTMLInputElement).checked;
        setIsFull(!checked);
    };

    useEffect(() => {

        const onKeyDown = async (evt: KeyboardEvent) => {

            if(evt.code === 'KeyS' && (evt.ctrlKey || evt.metaKey)) {
                const $box = (evt.target as HTMLElement).closest('[data-type="upsert-storage-place"]');
                if(!$box) return;

                evt.preventDefault();
                await save();
                return;
            }

            if(evt.code === 'Escape') {
                evt.stopPropagation();

                if(close) {
                    close();
                }

                return;
            }
        };

        document.addEventListener('keydown', onKeyDown, true);

        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
        };

    }, [save, close]);

    return (
        <div data-type="upsert-storage-place">
            <label className="flex flex-col mb-4">
                <span className="font-bold text-slate-400 mb-2">Storage Place Name</span>
                <input
                    ref={ nameRef }
                    value={ name }
                    onInput={ onNameChange }
                    className={ `border rounded px-4 py-2 ${ isNameValid ? 'outline-stone-200' : 'outline-red-200 border-red-200' }` }
                    type="text"
                />

                {
                    !isNameValid && <div className="text-red-700 text-xs mt-1">The name is required.</div>
                }
            </label>

            <div className="flex flex-col mb-4">
                <div className="font-bold text-slate-400 mb-2">Storage Place Description</div>
                <div className="markdown-editor border rounded-lg">
                    <MDXEditor
                        ref={ editorRef }
                        markdown={ '' }
                        plugins={ getCompactMarkdownEditorPlugins() }
                    />
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                <label className="flex flex-col mb-4">
                    <span className="font-bold text-slate-400 mb-2">Width</span>
                    <input
                        value={ width }
                        onInput={ onWidthChange }
                        className={ `border rounded px-4 py-2` }
                        type="text"
                    />
                </label>

                <label className="flex flex-col mb-4">
                    <span className="font-bold text-slate-400 mb-2">Height</span>
                    <input
                        value={ height }
                        onInput={ onHeightChange }
                        className={ `border rounded px-4 py-2` }
                        type="text"
                    />
                </label>

                <label className="flex flex-col mb-4">
                    <span className="font-bold text-slate-400 mb-2">Depth</span>
                    <input
                        value={ depth }
                        onInput={ onDepthChange }
                        className={ `border rounded px-4 py-2` }
                        type="text"
                    />
                </label>
            </div>

            <label className="flex items-center ml-2 md:ml-auto text-xs md:text-sm">
                <input
                    className="mr-2"
                    checked={ isFull }
                    type="checkbox"
                    onInput={ onIsFullChange }
                />

                Is Full
            </label>

            <div className="flex items-center justify-end text-sm mt-4">

                {
                    isLoading &&
                    <PreloaderIcon size={ 24 } color={ '#717985' }/>
                }

                {
                    !isLoading &&
                    <>
                        {
                            close &&
                            <button
                                className="bg-slate-500 text-slate-100 rounded px-6 py-2 mr-4"
                                onClick={ close }
                                type="button">Close</button>
                        }

                        <button
                            className="bg-teal-500 text-slate-100 rounded px-6 py-2"
                            onClick={ save }
                            type="button">Save
                        </button>
                    </>
                }
            </div>

        </div>
    )
};

export default UpsertStoragePlace;